// src/UserInfo.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Chip,
  Snackbar,
  Alert,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/system';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { serverTimestamp } from 'firebase/firestore';
import { useAuth } from './AuthContext';
import { saveUserSingleDoc, loadUserSingleDoc } from './firestore';

const PrimaryButton = styled(Button)({
  borderRadius: 12,
  padding: '10px 16px',
  textTransform: 'none',
  fontWeight: 800,
  boxShadow: 'none',
  backgroundColor: '#000',
  color: '#fff',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#222',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
  },
  '&:disabled': { backgroundColor: '#111', color: 'rgba(255,255,255,0.6)' },
});

const SubtleButton = styled(Button)({
  borderRadius: 12,
  padding: '10px 16px',
  textTransform: 'none',
  fontWeight: 800,
  boxShadow: 'none',
  backgroundColor: '#fff',
  color: '#000',
  border: '1px solid rgba(0,0,0,0.15)',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
  '&:hover': {
    backgroundColor: '#fafafa',
    transform: 'translateY(-1px)',
    boxShadow: '0 10px 24px rgba(0,0,0,0.08)',
  },
});

const SoftCard = ({ title, icon, children }) => (
  <Paper
    elevation={0}
    sx={{
      p: { xs: 2, sm: 2.5 },
      borderRadius: 4,
      border: '1px solid rgba(0,0,0,0.08)',
      bgcolor: 'white',
      boxShadow: '0 18px 45px rgba(0,0,0,0.05)',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mb: 1.5, flexWrap: 'wrap' }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 999,
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'rgba(0,0,0,0.06)',
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 900 }}>
        {title}
      </Typography>
    </Box>

    <Divider sx={{ mb: 2 }} />

    {children}
  </Paper>
);

const StatPill = ({ label, value }) => (
  <Paper
    elevation={0}
    sx={{
      px: 1.4,
      py: 0.9,
      borderRadius: 999,
      border: '1px solid rgba(0,0,0,0.08)',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      bgcolor: 'rgba(255,255,255,0.85)',
      backdropFilter: 'blur(6px)',
    }}
  >
    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', fontWeight: 700 }}>
      {label}
    </Typography>
    <Chip
      size="small"
      label={value}
      sx={{
        fontWeight: 900,
        bgcolor: 'rgba(0,0,0,0.06)',
        borderRadius: 999,
      }}
    />
  </Paper>
);

const emptyUserInfo = {
  name: '',
  ndisProviderNumber: '',
  abn: '',
  bankDetails: '',
  address: '',
  email: '',
};

const isValidABN = (abnRaw) => {
  const abn = (abnRaw || '').replace(/\D/g, '');
  return /^\d{11}$/.test(abn);
};

const UserInfo = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [userInfo, setUserInfo] = useState(emptyUserInfo);
  const [initialLoaded, setInitialLoaded] = useState(emptyUserInfo);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const showToast = (severity, message) => setToast({ open: true, severity, message });

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    const loadData = async () => {
      try {
        const loaded = await loadUserSingleDoc(currentUser.uid, 'userInfo', emptyUserInfo);
        const merged = { ...emptyUserInfo, ...(loaded || {}) };
        setUserInfo(merged);
        setInitialLoaded(merged);
      } catch (err) {
        console.error('Error loading user info:', err);
        showToast('error', 'Failed to load provider details.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, navigate]);

  const handleChange = (field) => (e) => {
    setUserInfo((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const cleanedForSave = useMemo(() => {
    const trim = (v) => (v || '').trim();

    return {
      name: trim(userInfo.name),
      ndisProviderNumber: trim(userInfo.ndisProviderNumber),
      abn: trim(userInfo.abn).replace(/\D/g, ''),
      bankDetails: trim(userInfo.bankDetails),
      address: trim(userInfo.address),
      email: trim(userInfo.email),
      updatedAt: serverTimestamp(),
    };
  }, [userInfo]);

  const requiredOk =
    !!cleanedForSave.name &&
    !!cleanedForSave.abn &&
    !!cleanedForSave.bankDetails &&
    !!cleanedForSave.address;

  const abnOk = !cleanedForSave.abn ? false : isValidABN(cleanedForSave.abn);

  const dirty = useMemo(() => {
    const normalize = (obj) => ({
      name: (obj.name || '').trim(),
      ndisProviderNumber: (obj.ndisProviderNumber || '').trim(),
      abn: (obj.abn || '').replace(/\D/g, ''),
      bankDetails: (obj.bankDetails || '').trim(),
      address: (obj.address || '').trim(),
      email: (obj.email || '').trim(),
    });

    const a = normalize(userInfo);
    const b = normalize(initialLoaded);
    return JSON.stringify(a) !== JSON.stringify(b);
  }, [userInfo, initialLoaded]);

  const completion = useMemo(() => {
  const checks = [
    !!cleanedForSave.name,
    !!cleanedForSave.abn && abnOk,
    !!cleanedForSave.bankDetails,
    !!cleanedForSave.address,
  ];
  const done = checks.filter(Boolean).length;
  return { done, total: checks.length };
}, [cleanedForSave, abnOk]);


  const copyToClipboard = async (value, label) => {
    try {
      await navigator.clipboard.writeText(String(value || ''));
      showToast('success', `${label} copied.`);
    } catch {
      showToast('error', `Could not copy ${label}.`);
    }
  };

  const handleSave = async () => {
    if (!requiredOk) {
      showToast('warning', 'Please fill required fields: Name, ABN, Bank Details, Address.');
      return;
    }
    if (!abnOk) {
      showToast('warning', 'ABN must be exactly 11 digits.');
      return;
    }

    setSaving(true);
    try {
      await saveUserSingleDoc(currentUser.uid, 'userInfo', cleanedForSave);
      setInitialLoaded({ ...userInfo, abn: cleanedForSave.abn });
      showToast('success', 'Provider details saved.');
    } catch (err) {
      console.error('Save failed:', err);
      showToast('error', `Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading provider details...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: '1200px', mx: 'auto' }}>
      {/* Premium header */}
      <Box
        sx={{
          mb: 2.5,
          p: 2.5,
          borderRadius: 4,
          border: '1px solid rgba(0,0,0,0.08)',
          background:
            'linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(245,247,255,1) 45%, rgba(245,255,248,1) 100%)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <SubtleButton onClick={() => navigate('/')} startIcon={<ArrowBackIcon />}>
            Back
          </SubtleButton>

          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.02em', lineHeight: 1.1 }}>
              Provider Details
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)', mt: 0.5 }}>
              These details appear on invoices and client documents.
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
              <StatPill label="Completion" value={`${completion.done}/${completion.total}`} />
              {dirty ? (
                <Chip
                  size="small"
                  label="Unsaved changes"
                  color="warning"
                  sx={{ fontWeight: 900, borderRadius: 999 }}
                />
              ) : (
                <Chip
                  size="small"
                  label="Saved"
                  sx={{ fontWeight: 900, borderRadius: 999, bgcolor: 'rgba(46,125,50,0.10)', color: 'rgb(46,125,50)' }}
                />
              )}
            </Box>
          </Box>

          <PrimaryButton
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || !dirty || !requiredOk || !abnOk}
          >
            {saving ? 'Saving…' : 'Save'}
          </PrimaryButton>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gap: 2 }}>
        <SoftCard title="Business" icon={<BusinessIcon fontSize="small" />}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              label="Business / Provider Name"
              value={userInfo.name}
              onChange={handleChange('name')}
              required
              sx={{ width: { xs: '100%', sm: 420 } }}
            />

            <TextField
              label="NDIS Provider Number (Optional)"
              value={userInfo.ndisProviderNumber}
              onChange={handleChange('ndisProviderNumber')}
              sx={{ width: { xs: '100%', sm: 300 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BadgeOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: userInfo.ndisProviderNumber ? (
                  <InputAdornment position="end">
                    <Tooltip title="Copy provider number">
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => copyToClipboard(userInfo.ndisProviderNumber, 'Provider number')}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />

            <TextField
              label="ABN (11 digits)"
              value={userInfo.abn}
              onChange={(e) => {
                // allow spaces while typing, but store as entered (cleaned on save)
                const next = e.target.value.replace(/[^\d\s]/g, '').slice(0, 14);
                setUserInfo((prev) => ({ ...prev, abn: next }));
              }}
              required
              error={!!userInfo.abn && !abnOk}
              helperText={!!userInfo.abn && !abnOk ? 'ABN must be exactly 11 digits' : 'Required'}
              sx={{ width: { xs: '100%', sm: 260 } }}
              InputProps={{
                endAdornment: cleanedForSave.abn ? (
                  <InputAdornment position="end">
                    <Tooltip title="Copy ABN">
                      <IconButton edge="end" size="small" onClick={() => copyToClipboard(cleanedForSave.abn, 'ABN')}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        </SoftCard>

        <SoftCard title="Bank details" icon={<AccountBalanceIcon fontSize="small" />}>
          <TextField
            label="Bank details (BSB & Account Number)"
            value={userInfo.bankDetails}
            onChange={handleChange('bankDetails')}
            required
            fullWidth
            multiline
            minRows={3}
            placeholder={`Example:\nBSB: 123-456\nAccount: 12345678\nAccount Name: Your Business`}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1.5 }}>
            <Chip
              size="small"
              label="Tip: Keep it short for invoice footer"
              sx={{ borderRadius: 999, fontWeight: 800, bgcolor: 'rgba(0,0,0,0.06)' }}
            />
            {cleanedForSave.bankDetails && (
              <SubtleButton
                onClick={() => copyToClipboard(cleanedForSave.bankDetails, 'Bank details')}
                startIcon={<ContentCopyIcon />}
              >
                Copy
              </SubtleButton>
            )}
          </Box>
        </SoftCard>

        <SoftCard title="Address & contact" icon={<HomeOutlinedIcon fontSize="small" />}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <TextField
              label="Business address"
              value={userInfo.address}
              onChange={handleChange('address')}
              required
              multiline
              minRows={3}
              sx={{ width: { xs: '100%', md: 640 } }}
            />

            <TextField
              label="Email (Optional)"
              value={userInfo.email}
              onChange={handleChange('email')}
              sx={{ width: { xs: '100%', sm: 360 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MailOutlineIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </SoftCard>

        {/* Bottom save bar */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 4,
            border: '1px solid rgba(0,0,0,0.08)',
            bgcolor: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontWeight: 900 }}>
            {dirty ? 'You have unsaved changes.' : 'All changes saved.'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.65)' }}>
            Required: Name, ABN, Bank Details, Address.
          </Typography>

          <Box sx={{ flex: 1 }} />

          <PrimaryButton
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || !dirty || !requiredOk || !abnOk}
          >
            {saving ? 'Saving…' : 'Save'}
          </PrimaryButton>
        </Paper>
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={2600}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserInfo;



